import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useMapStore } from "@/store/useMapStore";
import { CustomizerConfig } from "@/config/customizerConfig";
import { Palette, RotateCcw } from "lucide-react";
import { Switch } from "@/components/ui/switch";

interface AdvancedTabProps {
  config: CustomizerConfig;
}

const AdvancedTab = ({ config }: AdvancedTabProps) => {
  const { 
    colorCustomization, 
    setColorCustomization, 
    selectedPalette, 
    setPalette,
    hasCustomColors 
  } = useMapStore();

  const handleColorChange = (colorType: 'mapColor' | 'textColor' | 'baseBoardColor', value: string) => {
    setColorCustomization({ [colorType]: value });
  };

  const handleResetToPalette = () => {
    // Reset to current palette colors
    setPalette(selectedPalette);
  };

  const { palettes } = useMapStore();
  const currentPalette = palettes.find(p => p.id === selectedPalette);

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Advanced</h1>
        <p className="text-gray-600 mb-6">
          Fine-tune your map with advanced customization options.
        </p>
        
        {/* Custom Colors Section */}
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide">Custom Colors</h3>
              {currentPalette && (
                <p className="text-xs text-gray-600 mt-1">
                  Currently using: <span className="font-medium">{currentPalette.displayName}</span>
                </p>
              )}
            </div>
            {hasCustomColors && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetToPalette}
                className="flex items-center gap-2"
              >
                <RotateCcw className="w-3 h-3" />
                Reset to Palette
              </Button>
            )}
          </div>
          <p className="text-xs text-gray-600">
            Customize individual colors below to override the selected palette.
          </p>
        </div>
        
        {/* Map Color */}
        <div className="mb-6">
          <Label className="block text-sm font-medium text-gray-700 mb-2 uppercase tracking-wide">
            <Palette className="inline-block w-4 h-4 mr-2" />
            MAP COLOR
          </Label>
          <p className="text-xs text-gray-500 mb-3">
            This color will be applied to all map elements (streets, buildings, etc.)
          </p>
          <div className="flex items-center gap-3">
            <Input
              type="color"
              value={colorCustomization.mapColor}
              onChange={(e) => handleColorChange('mapColor', e.target.value)}
              className="w-16 h-12 border-gray-200 focus:border-gray-400 rounded-lg cursor-pointer"
            />
            <Input
              type="text"
              value={colorCustomization.mapColor}
              onChange={(e) => handleColorChange('mapColor', e.target.value)}
              placeholder="#0092cc"
              className="flex-1 h-12 bg-white border-gray-200 focus:border-gray-400 font-mono"
            />
          </div>
        </div>
        
        {/* Text Color */}
        <div className="mb-6">
          <Label className="block text-sm font-medium text-gray-700 mb-2 uppercase tracking-wide">
            <Palette className="inline-block w-4 h-4 mr-2" />
            TEXT COLOR
          </Label>
          <p className="text-xs text-gray-500 mb-3">
            Color for headlines, dividers, and taglines
          </p>
          <div className="flex items-center gap-3">
            <Input
              type="color"
              value={colorCustomization.textColor}
              onChange={(e) => handleColorChange('textColor', e.target.value)}
              className="w-16 h-12 border-gray-200 focus:border-gray-400 rounded-lg cursor-pointer"
            />
            <Input
              type="text"
              value={colorCustomization.textColor}
              onChange={(e) => handleColorChange('textColor', e.target.value)}
              placeholder="#000000"
              className="flex-1 h-12 bg-white border-gray-200 focus:border-gray-400 font-mono"
            />
          </div>
        </div>
        
        {/* Base Board Color */}
        <div className="mb-6">
          <Label className="block text-sm font-medium text-gray-700 mb-2 uppercase tracking-wide">
            <Palette className="inline-block w-4 h-4 mr-2" />
            BASE BOARD COLOR
          </Label>
          <p className="text-xs text-gray-500 mb-3">
            Background color of the map board/canvas
          </p>
          <div className="flex items-center gap-3">
            <Input
              type="color"
              value={colorCustomization.baseBoardColor}
              onChange={(e) => handleColorChange('baseBoardColor', e.target.value)}
              className="w-16 h-12 border-gray-200 focus:border-gray-400 rounded-lg cursor-pointer"
            />
            <Input
              type="text"
              value={colorCustomization.baseBoardColor}
              onChange={(e) => handleColorChange('baseBoardColor', e.target.value)}
              placeholder="#ffffff"
              className="flex-1 h-12 bg-white border-gray-200 focus:border-gray-400 font-mono"
            />
          </div>
        </div>

        {/* Color Preview */}
        <div className="mt-8 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h3 className="text-sm font-medium text-gray-700 mb-3 uppercase tracking-wide">Color Preview</h3>
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center">
              <div 
                className="w-12 h-12 rounded-lg border border-gray-300 shadow-sm"
                style={{ backgroundColor: colorCustomization.mapColor }}
              ></div>
              <span className="text-xs text-gray-500 mt-1">Map</span>
            </div>
            <div className="flex flex-col items-center">
              <div 
                className="w-12 h-12 rounded-lg border border-gray-300 shadow-sm flex items-center justify-center text-xs font-bold"
                style={{ 
                  backgroundColor: colorCustomization.baseBoardColor,
                  color: colorCustomization.textColor 
                }}
              >
                Aa
              </div>
              <span className="text-xs text-gray-500 mt-1">Text</span>
            </div>
            <div className="flex flex-col items-center">
              <div 
                className="w-12 h-12 rounded-lg border border-gray-300 shadow-sm"
                style={{ backgroundColor: colorCustomization.baseBoardColor }}
              ></div>
              <span className="text-xs text-gray-500 mt-1">Board</span>
            </div>
          </div>
          {hasCustomColors && (
            <p className="text-xs text-amber-600 mt-3 font-medium">
              ⚠️ Custom colors are active and will override palette selection
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedTab;